import { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ArrowDown, Check } from "@/shared/icons";

/**
 * Slide-to-confirm control. Drag the thumb across the track to fire a
 * deliberate, hard-to-misclick action (e.g. mark-in / mark-out).
 *
 * Keyboard: focus + Enter/Space triggers immediately.
 * Async: returning a Promise from `onConfirm`; reject auto-snaps the thumb
 * back. Pass `loading=true` to keep the thumb at the end while the parent
 * awaits its own pipeline.
 */
type Tone = "success" | "warning" | "danger" | "primary";

const PALETTES: Record<
  Tone,
  { trackBg: string; trackBorder: string; trackText: string; fill: string; thumb: string; thumbDone: string }
> = {
  success: {
    trackBg: "#ecfdf5",
    trackBorder: "#10b981",
    trackText: "#047857",
    fill: "#10b981",
    thumb: "#047857",
    thumbDone: "#065f46",
  },
  primary: {
    trackBg: "#eff6ff",
    trackBorder: "var(--accent-600, #2563eb)",
    trackText: "#1d4ed8",
    fill: "var(--accent-600, #2563eb)",
    thumb: "#1d4ed8",
    thumbDone: "#1e40af",
  },
  warning: {
    trackBg: "#fff7ed",
    trackBorder: "#f59e0b",
    trackText: "#92400e",
    fill: "#d97706",
    thumb: "#92400e",
    thumbDone: "#b45309",
  },
  danger: {
    trackBg: "#fef2f2",
    trackBorder: "#ef4444",
    trackText: "#991b1b",
    fill: "#dc2626",
    thumb: "#991b1b",
    thumbDone: "#b91c1c",
  },
};

export interface SlideToConfirmProps {
  label?: string;
  completedLabel?: string;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  tone?: Tone;
  ariaLabel?: string;
}

export function SlideToConfirm({
  label = "Slide to confirm",
  completedLabel = "Confirming…",
  onConfirm,
  loading = false,
  disabled = false,
  tone = "primary",
  ariaLabel,
}: SlideToConfirmProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const TRACK_HEIGHT = isXs ? 44 : 48;
  const THUMB_SIZE = isXs ? 38 : 42;
  const PADDING = 3;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(0);
  const [maxX, setMaxX] = useState(0);
  const [completed, setCompleted] = useState(false);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const posRef = useRef(0);
  const maxXRef = useRef(0);
  const stateRef = useRef({ disabled, loading, completed });
  stateRef.current = { disabled, loading, completed };

  const palette = PALETTES[tone];

  const recomputeMax = useCallback(() => {
    if (!trackRef.current) return 0;
    const m = trackRef.current.offsetWidth - THUMB_SIZE - PADDING * 2;
    const clamped = Math.max(0, m);
    maxXRef.current = clamped;
    setMaxX(clamped);
    return clamped;
  }, [THUMB_SIZE]);

  useEffect(() => {
    recomputeMax();
    const node = trackRef.current;
    if (!node) return undefined;
    const ro = new ResizeObserver(recomputeMax);
    ro.observe(node);
    return () => ro.disconnect();
  }, [recomputeMax]);

  // When parent's `loading` clears AND we never set `completed`, snap back.
  useEffect(() => {
    if (!loading && !completed) {
      posRef.current = 0;
      setPos(0);
    }
  }, [loading, completed]);

  const updatePos = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(next, maxXRef.current));
    posRef.current = clamped;
    setPos(clamped);
  }, []);

  const onStart = (clientX: number) => {
    const { disabled: d, loading: l, completed: c } = stateRef.current;
    if (d || l || c) return;
    draggingRef.current = true;
    startXRef.current = clientX - posRef.current;
  };

  const fire = useCallback(() => {
    setCompleted(true);
    Promise.resolve(onConfirm?.()).catch(() => {
      setCompleted(false);
      updatePos(0);
    });
  }, [onConfirm, updatePos]);

  const onEnd = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const limit = maxXRef.current;
    if (posRef.current >= limit - 4) {
      updatePos(limit);
      fire();
    } else {
      updatePos(0);
    }
  }, [fire, updatePos]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      updatePos(e.clientX - startXRef.current);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!draggingRef.current) return;
      const t = e.touches[0];
      if (!t) return;
      updatePos(t.clientX - startXRef.current);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [onEnd, updatePos]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { disabled: d, loading: l, completed: c } = stateRef.current;
    if (d || l || c) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      updatePos(maxXRef.current);
      fire();
    }
  };

  const fillWidth = pos + THUMB_SIZE + PADDING;
  const trackTextOpacity = completed ? 0 : Math.max(0, 1 - pos / Math.max(maxX * 0.6, 1));
  const labelFontSize = isXs ? 12 : 13;

  return (
    <Box
      ref={trackRef}
      role="slider"
      tabIndex={disabled || loading || completed ? -1 : 0}
      aria-label={ariaLabel ?? label}
      aria-disabled={disabled || loading}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={maxX > 0 ? Math.round((pos / maxX) * 100) : 0}
      onKeyDown={handleKeyDown}
      sx={{
        position: "relative",
        height: TRACK_HEIGHT,
        width: "100%",
        bgcolor: palette.trackBg,
        border: `1px solid ${palette.trackBorder}`,
        borderRadius: 999,
        overflow: "hidden",
        userSelect: "none",
        touchAction: "pan-y",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled || loading ? "not-allowed" : "default",
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${palette.trackBorder}33`,
        },
      }}
    >
      {/* Fill */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: `${fillWidth}px`,
          bgcolor: palette.fill,
          borderRadius: 999,
          transition: draggingRef.current ? "none" : "width 0.2s",
          pointerEvents: "none",
        }}
      />

      {/* Idle label */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: palette.trackText,
          gap: 0.75,
          px: 6,
          pointerEvents: "none",
          opacity: trackTextOpacity,
          transition: "opacity 0.1s",
        }}
      >
        <ArrowDown size={isXs ? 14 : 16} style={{ transform: "rotate(-90deg)" }} />
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 600, color: "inherit", fontSize: labelFontSize }}
        >
          {label}
        </Typography>
      </Box>

      {/* Completed label */}
      {completed && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            gap: 0.75,
            px: 6,
            pointerEvents: "none",
          }}
        >
          {loading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Check size={isXs ? 14 : 16} />}
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, color: "inherit", fontSize: labelFontSize }}
          >
            {completedLabel}
          </Typography>
        </Box>
      )}

      {/* Thumb */}
      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          onStart(e.clientX);
        }}
        onTouchStart={(e) => {
          const t = e.touches[0];
          if (t) onStart(t.clientX);
        }}
        sx={{
          position: "absolute",
          top: `${PADDING}px`,
          left: `${PADDING}px`,
          width: `${THUMB_SIZE}px`,
          height: `${THUMB_SIZE}px`,
          bgcolor: "#ffffff",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: completed ? palette.thumbDone : palette.thumb,
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          cursor: disabled || loading || completed ? "default" : "grab",
          transform: `translateX(${pos}px)`,
          transition: draggingRef.current ? "none" : "transform 0.2s",
          zIndex: 2,
          touchAction: "none",
          "&:active": {
            cursor: disabled || loading || completed ? "default" : "grabbing",
          },
        }}
      >
        {loading && completed ? (
          <CircularProgress size={isXs ? 16 : 18} sx={{ color: palette.thumbDone }} />
        ) : (
          <ArrowDown size={isXs ? 20 : 22} style={{ transform: "rotate(-90deg)" }} />
        )}
      </Box>
    </Box>
  );
}
